import { Button } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { Document } from '@creditornot/cb-icons';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { localize } from 'common/utils';
import {
  googleSheetPageInteractionClickCancelSheetImport,
  googleSheetPageInteractionClickUploadSheetData,
} from 'core/telemetry/avo/Avo';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { buildGetSelectionAllQueryKey } from 'modules/shared-menu/hooks/useSelectionData';
import { useGoogleSheetPageViewTracking } from 'modules/shared-menu/telemetry';
import { SharedMenu } from 'modules/shared-menu/types';

import { ColumnsPickerModal } from './components/ColumnsPickerModal';
import { DataTable } from './components/DataTable';
import { tableView } from './components/DataTable/constants';
import { useDataTable } from './components/DataTable/useDataTable';
import { ExpandableView } from './components/ExpandableView';
import * as S from './components/GoogleSheetsImport.styles';
import { ImportEntryPoint } from './components/ImportEntryPoint';
import { ImportSummary } from './components/ImportSummary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { Stepper } from './components/Stepper';
import { GOOGLE_SHEETS_TOAST_ERROR_ID, GOOGLE_SHEETS_TOAST_SUCCESS_ID } from './constants';
import { useGoogleSheetsImport } from './hooks/useGoogleSheetsImport';
import { SheetNameToShapedLinesMap } from './libs/googleSheets';
import { getSummaryInfo } from './utils';
import { CustomToast } from '../../components/CustomToast';

type GoogleSheetsImportProps = {
  sharedMenu: SharedMenu;
};

export const GoogleSheetsImportPage = ({ sharedMenu }: GoogleSheetsImportProps) => {
  const history = useHistory();
  const intl = useIntl();
  const { menuEditorPage } = useListingManagerRoutes();
  const [showSummary, setShowSummary] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isColumnsPickerModalOpen, setIsColumnsPickerModalOpen] = useState(false);
  const [exportParam, setExportParam] = useQueryParam('export', StringParam);

  const queryClient = useQueryClient();
  useGoogleSheetPageViewTracking();
  const handleGenericError = useCallback(
    (message?: string) => {
      toast.custom(
        <CustomToast
          variant="error"
          title={intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.error' })}
          toastId={GOOGLE_SHEETS_TOAST_ERROR_ID}
          message={message}
        />,
        { id: GOOGLE_SHEETS_TOAST_ERROR_ID, duration: Infinity },
      );
    },
    [intl],
  );

  const {
    offers,
    options,
    categories,
    isDownloadingAndValidating,
    isGeneratingSpreadsheetFromMenu,
    isDoneValidating,
    isEmptySpreadsheet,
    selectedSpreadsheetFile,
    openSpreadsheetPickerModal,
    createSpreadsheetFromExistingMenu,
    uploadData,
    resetProcess,
    isUploading,
    isStartedSelectingSheet,
  } = useGoogleSheetsImport({
    sharedMenu,
    onUploadProgressChange: setUploadProgress,
    onRequestError: handleGenericError,
  });

  useEffect(() => {
    if (isDoneValidating && currentStep === 1 && selectedSpreadsheetFile) {
      if (isEmptySpreadsheet) {
        toast.error(intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.empty-sheet' }));
        resetProcess();
      } else {
        setCurrentStep(2);
      }
      setUploadProgress(0);
    }
  }, [
    categories,
    currentStep,
    isDoneValidating,
    offers,
    options,
    selectedSpreadsheetFile,
    isEmptySpreadsheet,
    intl,
    resetProcess,
  ]);

  useEffect(() => {
    if (exportParam === 'true') {
      setExportParam(undefined);
      setIsColumnsPickerModalOpen(true);
    }
  }, [exportParam, setExportParam]);

  const handleSelectSheetClick = useCallback(() => {
    openSpreadsheetPickerModal();
  }, [openSpreadsheetPickerModal]);

  const handleSelectTemplateClick = () => {
    setIsColumnsPickerModalOpen(true);
  };

  const dataTableState = useDataTable({
    offers,
    options,
    categories,
  });

  const summaryInfo = getSummaryInfo({
    offers,
    options,
    categories,
    isDownloadingAndValidating,
  });

  const goBackToMenuEditor = useCallback(() => {
    history.push(menuEditorPage());
    resetProcess();
  }, [history, menuEditorPage, resetProcess]);

  const startOver = useCallback(() => {
    setCurrentStep(1);
    setShowSummary(true);
    resetProcess();
  }, [resetProcess]);

  const handleUploadData = useCallback(() => {
    googleSheetPageInteractionClickUploadSheetData({
      numberOfOffers: (offers.data ?? []).length,
      numberOfOptions: (options.data ?? []).length,
      numberOfCategories: (categories.data ?? []).length,
    });
    uploadData({
      onSuccess: () => {
        toast.custom(
          <CustomToast
            variant="success"
            title={intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.success' })}
            message={`Google Sheet (${selectedSpreadsheetFile?.properties?.title ?? ''})`}
            toastId={GOOGLE_SHEETS_TOAST_SUCCESS_ID}
          />,
          { id: GOOGLE_SHEETS_TOAST_SUCCESS_ID },
        );
        queryClient.removeQueries({ queryKey: buildGetSelectionAllQueryKey(sharedMenu.id, true) });
        goBackToMenuEditor();
      },
      onError: () => {
        toast.custom(
          <CustomToast
            variant="error"
            title={intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.error' })}
            message={`Google Sheet (${selectedSpreadsheetFile?.properties?.title ?? ''})`}
            toastId={GOOGLE_SHEETS_TOAST_ERROR_ID}
            linkText={intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.try-again' })}
            onLinkClick={startOver}
          />,
          { id: GOOGLE_SHEETS_TOAST_ERROR_ID, duration: Infinity },
        );
      },
    });
  }, [
    categories.data,
    goBackToMenuEditor,
    intl,
    offers.data,
    options.data,
    queryClient,
    selectedSpreadsheetFile?.properties?.title,
    sharedMenu.id,
    startOver,
    uploadData,
  ]);

  return (
    <S.Root>
      <S.HeaderContainer>
        <S.Header>
          <S.StyledTextButton variant="black" size="small" icon={<ArrowLeft />} onClick={goBackToMenuEditor}>
            <FormattedMessage id="shared-menu.editor.back-button" />
          </S.StyledTextButton>
          <FormattedMessage id="shared-menu.editor.importer.page-header" />
        </S.Header>
        <S.MenuNameTag icon={<Document />}>{localize(sharedMenu.name, sharedMenu.primary_language)}</S.MenuNameTag>
      </S.HeaderContainer>
      <S.Content>
        {!isGeneratingSpreadsheetFromMenu && <Stepper currentStep={currentStep} />}
        {isDownloadingAndValidating || isGeneratingSpreadsheetFromMenu || isUploading ? (
          <LoadingSpinner
            isGeneratingSpreadsheetFromMenu={isGeneratingSpreadsheetFromMenu}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          />
        ) : (
          <>
            {currentStep === 1 ? (
              <ImportEntryPoint
                onSelectSheetClick={handleSelectSheetClick}
                onSelectTemplateClick={handleSelectTemplateClick}
                isSelectSheetLoading={isStartedSelectingSheet}
              />
            ) : (
              <>
                {showSummary && (
                  <ImportSummary importedFileName={selectedSpreadsheetFile?.properties?.title ?? ''} {...summaryInfo} />
                )}
                <ExpandableView
                  title={intl.formatMessage({ id: 'shared-menu.editor.importer.advanced-view.title' })}
                  description={intl.formatMessage({ id: 'shared-menu.editor.importer.advanced-view.body' })}
                  onExpandClick={() => setShowSummary(prevState => !prevState)}
                >
                  <SegmentedControl
                    style={{ alignSelf: 'flex-start' }}
                    onChange={dataTableState.setCurrentView}
                    options={[
                      {
                        disabled: false,
                        label: intl.formatMessage({ id: 'shared-menu.editor.importer.models.offers' }),
                        value: tableView.OFFERS,
                      },
                      {
                        disabled: false,
                        label: intl.formatMessage({ id: 'shared-menu.editor.importer.models.options' }),
                        value: tableView.OPTIONS,
                      },
                      {
                        disabled: false,
                        label: intl.formatMessage({ id: 'shared-menu.editor.importer.models.categories' }),
                        value: tableView.CATEGORIES,
                      },
                    ]}
                    value={dataTableState.currentView}
                  />
                  <DataTable state={dataTableState} />
                </ExpandableView>
                <S.ButtonsContainer>
                  <Button
                    variant="lightBlue"
                    size="small"
                    onClick={() => {
                      googleSheetPageInteractionClickCancelSheetImport({
                        numberOfOffers: (offers.data ?? []).length,
                        numberOfOptions: (options.data ?? []).length,
                        numberOfCategories: (categories.data ?? []).length,
                      });
                      startOver();
                    }}
                  >
                    <FormattedMessage id="state.cancel" />
                  </Button>
                  <Button
                    variant="blue"
                    size="small"
                    onClick={handleUploadData}
                    disabled={summaryInfo.numberOfAdditions + summaryInfo.numberOfUpdates === 0}
                  >
                    <FormattedMessage id="shared-menu.editor.importer.table-view.upload-data" />
                  </Button>
                </S.ButtonsContainer>
              </>
            )}
          </>
        )}
      </S.Content>
      {isColumnsPickerModalOpen && (
        <ColumnsPickerModal
          onClose={() => setIsColumnsPickerModalOpen(false)}
          onConfirm={columnsToDisplay => {
            const selectedFields = {
              offers: columnsToDisplay.offers.map(({ value }) => value),
              options: columnsToDisplay.options.map(({ value }) => value),
              categories: columnsToDisplay.categories.map(({ value }) => value),
            } as SheetNameToShapedLinesMap;

            setIsColumnsPickerModalOpen(false);
            createSpreadsheetFromExistingMenu({
              onError: handleGenericError,
              selectedFields,
            });
          }}
        />
      )}
    </S.Root>
  );
};
